import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAlertConfig, useAlertStats } from '../../../api/queries';
import { post } from '../../../api/client';
import { Field } from '../../../components/Field';
import { Ic } from '../../../components/Ic';
import { toast } from '../../../components/Toast';
import { relTime } from '../../../lib/time';
import type { Ok } from '../../../api/types';

export function AlertsTab() {
  const { data: config } = useAlertConfig();
  const { data: stats } = useAlertStats();

  const [webhookUrl, setWebhookUrl] = useState('');
  const [suffix, setSuffix] = useState('');
  const [touched, setTouched] = useState(false);

  // Seed once the config arrives, without clobbering an in-progress edit.
  useEffect(() => {
    if (config && !touched) {
      setWebhookUrl(config.webhook_url ?? '');
      setSuffix(config.url_suffix ?? '');
    }
  }, [config, touched]);

  const saveConfig = useMutation({
    mutationFn: () => post<Ok>('/api/alerts/config', { webhook_url: webhookUrl, url_suffix: suffix }),
    onSuccess: () => {
      toast('Alert settings saved', 'success');
      setTouched(false);
    },
  });

  const testWebhook = useMutation({
    mutationFn: () => post<Ok>('/api/alerts/test-webhook'),
    onSuccess: () => toast('Test alert sent', 'success'),
  });

  return (
    <>
      <div className="pcard">
        <div className="head">
          <Ic.bell className="ic" />
          <h3>Sitemap alerts</h3>
          <span className="spacer" />
          <button
            className="btn sm"
            disabled={testWebhook.isPending || !webhookUrl}
            onClick={() => testWebhook.mutate()}
          >
            <Ic.zap className="ico" /> Send test
          </button>
        </div>
        <div className="body pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="muted" style={{ fontSize: 12.5 }}>
            The sitemap monitor scans robots.txt and sitemap.xml every 2 hours and reports URLs it
            hasn't seen before.
          </div>
          <Field label="Discord webhook URL" hint="where new-URL alerts are posted">
            <input
              className="input mono-sm"
              placeholder="https://discord.com/api/webhooks/…"
              value={webhookUrl}
              onChange={(e) => {
                setTouched(true);
                setWebhookUrl(e.target.value);
              }}
            />
          </Field>
          <Field label="URL suffix filter" hint="only alert on URLs ending with this">
            <input
              className="input mono-sm"
              placeholder="/raffle"
              value={suffix}
              onChange={(e) => {
                setTouched(true);
                setSuffix(e.target.value);
              }}
            />
          </Field>
          <div>
            <button
              className="btn primary sm"
              disabled={!touched || saveConfig.isPending}
              onClick={() => saveConfig.mutate()}
            >
              <Ic.check className="ico" /> {saveConfig.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <div className="pcard">
        <div className="head">
          <Ic.trend className="ic" />
          <h3>Alert volume</h3>
        </div>
        <div className="body pad">
          <table className="assign">
            <thead>
              <tr>
                <th>Source</th>
                <th>24h</th>
                <th>7d</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Sitemap</td>
                <td className="mono-sm">{stats?.last_24h.sitemap ?? '—'}</td>
                <td className="mono-sm">{stats?.last_7d.sitemap ?? '—'}</td>
                <td className="mono-sm">{stats?.totals.sitemap ?? '—'}</td>
              </tr>
              <tr>
                <td>Social</td>
                <td className="mono-sm">{stats?.last_24h.social ?? '—'}</td>
                <td className="mono-sm">{stats?.last_7d.social ?? '—'}</td>
                <td className="mono-sm">{stats?.totals.social ?? '—'}</td>
              </tr>
            </tbody>
          </table>
          {stats?.top_domain && (
            <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
              Most active domain: <strong>{stats.top_domain}</strong>
              {stats.newest ? ` · newest ${relTime(stats.newest)}` : ''}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
